import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown } from "lucide-react";

const slides = [
    {
        icon: <img src={`/assets/logo/icon_lacademik.png`} alt="Logo" className="w-20 h-auto mr-3" />,
        title: "Akademik",
        content: "Aplikasi Sistem Informasi Akademik Premium membantu mengelola data siswa, guru, jadwal, nilai, dan absensi secara mudah dan cepat.",
        screenshots: [
            // { src: "/assets/l`personalia/absensi_phone.png", caption: "Menu Admission" },
            { src: "/assets/l`akademik/student-home.png", caption: "Tampilan Home Mahasiswa/Pelajar" },
            { src: "/assets/l`akademik/student-spotlight.png", caption: "Tampilan Spotlight Mahasiswa/Pelajar" },
            { src: "/assets/l`akademik/teacher-dashboard.png", caption: "Tampilan Dashboard Dosen/Pengajar" },
            { src: "/assets/l`akademik/teacher-activity.png", caption: "Tampilan Activity Dosen/Pengajar" },
        ],
    },
    // {
    //     // icon: <img src={`/assets/logo/icon_lacademik.png`} alt="Logo" className="w-24 h-auto mr-3" />,
    //     title: "Personalia",
    //     content: [
    //         "Abasensi, Atur Shift, Atur Jadwal",
    //         "Penggajian, Atur Renumerasi"
    //     ],
    //     screenshots: [
    //         { src: "/assets/l`personalia/absensi_phone.png", caption: "Menu Absensi" }
    //     ],
    // },
    // {
    //     icon: <img src={`/assets/logo/icon_lpos.png`} alt="Logo" className="w-24 h-auto mr-3" />,
    //     title: "L`POS ",
    //     content: "Aplikasi kasir modern untuk kelola penjualan, stok, dan laporan bisnis",
    //     screenshots: [
    //         { src: "/assets/l`pos/pos.png", caption: "Menu Point Of Sales" }
    //     ],
    // },
];

export default function Catalog() {
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState("right");
    const [screenshotIndex, setScreenshotIndex] = useState(0);
    const [screenshotDirection, setScreenshotDirection] = useState("up");

    const currentScreenshots = slides[index].screenshots || [];

    const changeSlide = useCallback((delta, dir) => {
        setDirection(dir);
        setIndex((prev) => (prev + delta + slides.length) % slides.length);
        setScreenshotIndex(0);
    }, []);

    const changeScreenshot = useCallback(
        (delta, dir) => {
            if (currentScreenshots.length === 0) return;
            setScreenshotDirection(dir);
            setScreenshotIndex(
                (prev) =>
                    (prev + delta + currentScreenshots.length) %
                    currentScreenshots.length
            );
        },
        [currentScreenshots]
    );

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "ArrowRight") changeSlide(1, "right");
            else if (e.key === "ArrowLeft") changeSlide(-1, "left");
            else if (e.key === "ArrowUp") changeScreenshot(1, "up");
            else if (e.key === "ArrowDown") changeScreenshot(-1, "down");
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [changeSlide, changeScreenshot]);

    const slideVariants = {
        enter: (dir) => ({
            x: dir === "left" ? -150 : 150,
            opacity: 0,
            scale: 0.9,
        }),
        center: { x: 0, opacity: 1, scale: 1 },
        exit: (dir) => ({
            x: dir === "left" ? 150 : -150,
            opacity: 0,
            scale: 0.9,
        }),
    };

    const screenshotVariants = {
        enter: (dir) => ({
            y: dir === "up" ? -80 : 80,
            opacity: 0,
            scale: 0.95,
        }),
        center: { y: 0, opacity: 1, scale: 1 },
        exit: (dir) => ({
            y: dir === "up" ? 80 : -80,
            opacity: 0,
            scale: 0.95,
        }),
    };

    const renderContent = (content) => {
        if (Array.isArray(content)) {
            return (
                <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm md:text-base">
                    {content.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            );
        }
        return (
            <p className="text-gray-600 text-base md:text-lg leading-relaxed whitespace-pre-line">
                {content}
            </p>
        );
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-4 select-none relative">
            <div className="relative w-full max-w-6xl h-auto md:h-[550px] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row mt-20">
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={index}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        className={`flex flex-col md:flex-row w-full h-full`}
                    >
                        {/* Content Section */}
                        <div
                            className={`flex flex-col justify-center ${currentScreenshots.length > 0
                                ? "w-full md:w-1/2 px-4 md:px-10 py-6"
                                : "w-full text-center px-6 md:px-16 py-6"
                                }`}
                        >
                            <div className="flex items-center mb-3 md:mb-4 space-x-3">
                                {slides[index].icon && (
                                    <div className="flex-shrink-0 text-purple-500 text-3xl md:text-4xl">
                                        {slides[index].icon}
                                    </div>
                                )}
                                <h1 className="text-2xl md:text-4xl font-bold">
                                    {slides[index].title}
                                </h1>
                            </div>

                            <p>{renderContent(slides[index].content)}</p>
                        </div>

                        {/* Screenshot Section */}
                        {currentScreenshots.length > 0 && (
                            <div className="w-full md:w-1/2 flex flex-col items-center justify-center bg-gray-50 p-6 border-b md:border-b-0 md:border-r border-gray-200 relative">
                                <div className="relative w-full max-w-md h-[450px] md:h-[500px] overflow-hidden">
                                    <AnimatePresence mode="wait" custom={screenshotDirection}>
                                        <motion.img
                                            key={currentScreenshots[screenshotIndex].src}
                                            src={currentScreenshots[screenshotIndex].src}
                                            alt={`screenshot-${screenshotIndex}`}
                                            className="absolute w-full h-full object-contain"
                                            variants={screenshotVariants}
                                            initial="enter"
                                            animate="center"
                                            exit="exit"
                                            transition={{ duration: 0.5, ease: "easeInOut" }}
                                            custom={screenshotDirection}
                                        />
                                    </AnimatePresence>
                                </div>
                                <p className="mt-3 text-gray-600 text-sm italic text-center max-w-sm">
                                    {currentScreenshots[screenshotIndex].caption}
                                </p>

                                {currentScreenshots.length > 1 && (
                                    <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                                        {currentScreenshots.map((_, i) => (
                                            <div
                                                key={i}
                                                className={`w-2.5 h-2.5 rounded-full transition-all ${i === screenshotIndex
                                                    ? "bg-purple-500 scale-110"
                                                    : "bg-gray-300"
                                                    }`}
                                            ></div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* 🔹 Navigation Buttons */}
            {/* Desktop Layout */}
            <div className="absolute inset-0 hidden md:flex items-center justify-between px-8 z-20 pointer-events-none">
                <button
                    onClick={() => changeSlide(-1, "left")}
                    className="bg-[#f29f02] hover:bg-[#f29000] text-white p-3 rounded-full shadow-lg transition-all hover:scale-110 pointer-events-auto"
                >
                    <ArrowLeft />
                </button>
                <button
                    onClick={() => changeSlide(1, "right")}
                    className="bg-[#f29f02] hover:bg-[#f29000] text-white p-3 rounded-full shadow-lg transition-all hover:scale-110 pointer-events-auto"
                >
                    <ArrowRight />
                </button>
            </div>

            {/* 🔹 Mobile Layout */}
            <div className="fixed bottom-5 left-1/2 -translate-x-1/2 flex md:hidden items-center justify-center space-x-6 z-30">
                <button
                    onClick={() => changeSlide(-1, "left")}
                    className="bg-[#f29f02] hover:bg-[#f29000] text-white p-3 rounded-full shadow-md transition-all hover:scale-110"
                >
                    <ArrowLeft size={20} />
                </button>
                <button
                    onClick={() => changeSlide(1, "right")}
                    className="bg-[#f29f02] hover:bg-[#f29000] text-white p-3 rounded-full shadow-md transition-all hover:scale-110"
                >
                    <ArrowRight size={20} />
                </button>
            </div>

            {/* 🔹 Screenshot Navigation (Mobile + Desktop) */}
            {currentScreenshots.length > 1 && (
                <>
                    {/* Desktop */}
                    <div className="absolute bottom-[70px] right-4 hidden md:flex flex-col space-y-2 z-20">
                        <button
                            onClick={() => changeScreenshot(1, "up")}
                            className="bg-[#f29f02] hover:bg-[#f29000] text-white p-2 rounded-full shadow-md hover:scale-110"
                        >
                            <ArrowUp size={18} />
                        </button>
                        <button
                            onClick={() => changeScreenshot(-1, "down")}
                            className="bg-[#f29f02] hover:bg-[#f29000] text-white p-2 rounded-full shadow-md hover:scale-110"
                        >
                            <ArrowDown size={18} />
                        </button>
                    </div>

                    {/* Mobile */}
                    <div className="fixed right-4 bottom-[120px] md:hidden flex flex-col space-y-3 z-30">
                        <button
                            onClick={() => changeScreenshot(1, "up")}
                            className="bg-[#f29f02] hover:bg-[#f29000] text-white p-2 rounded-full shadow-md hover:scale-110"
                        >
                            <ArrowUp size={18} />
                        </button>
                        <button
                            onClick={() => changeScreenshot(-1, "down")}
                            className="bg-[#f29f02] hover:bg-[#f29000] text-white p-2 rounded-full shadow-md hover:scale-110"
                        >
                            <ArrowDown size={18} />
                        </button>
                    </div>
                </>
            )}


            {/* Footer Info */}
            <div className="absolute bottom-3 right-5 text-xs md:text-sm text-black">
                Product {index + 1} / {slides.length}
            </div>

            <p className="mt-4 text-gray-400 text-xs md:text-sm text-center">
                ← → : Ganti Produk &nbsp; | &nbsp; ↑ ↓ : Ganti Screenshot
            </p>
        </div>
    );
}